import { useState, useEffect, useContext } from "react"

import { Box, Slider, Typography, Card } from "@mui/material"

import { sliderConfigFull } from "../Data/answers"
import { sliderConfigLight } from "../Data/answers"
import { AnswersType } from "../Models/AnswersType"
import { updateAnswer } from "../Utilities/SetNewAnswer"
import { AnswerNumber } from "../Models/AnswerNumberType"

import { flexSpaceC } from "../Styles/EvaluationToolTheme"
import { flexCenterC } from "../Styles/EvaluationToolTheme"
import { PolyglotContext } from "../../../polyglot/polyGlotContext"
import { evaluationStageType } from "../Models/EvaluationStageType"

const AnswerSlider = ({ answerNumber, setCollectedAnswers, evaluationStage }: { answerNumber: AnswerNumber; setCollectedAnswers: React.Dispatch<React.SetStateAction<AnswersType>>, evaluationStage: evaluationStageType }) => {
    const { polyglot } = useContext(PolyglotContext)

    //State and Logic for handling events triggerd by movement of the slider.
    const [sliderValue, setSliderValue] = useState<number>(3)

    let sliderConfig = sliderConfigFull;
    if (evaluationStage === "beforeLight" || evaluationStage === "afterLight") {
        sliderConfig = sliderConfigLight;
    }

    useEffect(() => {
        const initialAnswerData = sliderConfig[answerNumber][2].answerData
        setSliderValue(initialAnswerData.value)
        updateAnswer({ [answerNumber]: initialAnswerData }, setCollectedAnswers)
    }, [answerNumber])

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        const newValueNum = newValue as number
        setSliderValue(newValueNum)
    }

    const handleCommittedSliderChange = (event: Event | React.SyntheticEvent<Element, Event>, newValue: number | number[]) => {
        const newValueNum = newValue as number
        const selectedAnswerData = sliderConfig[answerNumber][newValueNum - 1].answerData
        updateAnswer({ [answerNumber]: selectedAnswerData }, setCollectedAnswers)
    }

    const { imgAlt = "", imgUrl = "", typographyTextContent = "", } = sliderConfig[answerNumber][sliderValue - 1] ?? {}

    return (
        <Box
            sx={{
                ...flexSpaceC,
                width: "100%",
                height: "100%",
                mt: 4,
                mb: 4,
            }}
        >
            <Card
                sx={{
                    ...flexCenterC,
                    justifyContent: "flex-start",
                    boxShadow: "none",
                    borderRadius: "none",
                    height: "100%",
                    width: "100%",
                }}
            >
                <img src={imgUrl} alt={imgAlt}
                    style={{
                        borderRadius: "20px", padding: "24px 16px 24px 16px", height: "75%", objectFit: "contain", cursor: "pointer"
                    }}
                />

                <Typography
                    variant="h2"
                    color="initial"
                    sx={{
                        textAlign: "center",
                    }}
                >
                    {polyglot.t(typographyTextContent)}
                </Typography>
            </Card>
            <Slider
                value={sliderValue}
                onChange={handleSliderChange}
                onChangeCommitted={handleCommittedSliderChange}
                min={1}
                max={5}
                step={1}
                defaultValue={3}
                valueLabelDisplay="auto"
                sx={{
                    width: "95%",
                    height: "1.5rem",
                    ...subSliderStylings,
                }}
            />
        </Box>
    )
}

const subSliderStylings = {
    "&.MuiSlider-root": {
        padding: "0",
    },

    "& .MuiSlider-thumb": {
        backgroundColor: "black",
        borderRadius: "50%",
        width: "4rem",
        height: "4rem",
    },

    "& .MuiSlider-rail": {
        background: `
            linear-gradient(
                to right,
                black 0%, black 1%,                            /* Start edge */

                #FF6B6B 1%, #FF6B6B 24.5%,                     /* Red */
                black 24.5%, black 25.5%,                      /* 25% divider */

                #FFA500 25.5%, #FFA500 49.5%,                 /* Orange */
                black 49.5%, black 50.5%,                      /* 50% divider */

                #FFD93D 50.5%, #FFD93D 74.5%,                 /* Yellow */
                black 74.5%, black 75.5%,                      /* 75% divider */

                #6BCB77 75.5%, #6BCB77 99%,                   /* Green */
                black 99%, black 100%                          /* End edge */
            )
        `,
        opacity: "1",
        borderRadius: "60px",
        height: "15px",
        border: "solid 2px black"
    },

    "& .MuiSlider-track": {
        display: "none",
    },

    "& .MuiSlider-mark": {
        display: "none",
    }
}

export default AnswerSlider
